import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { usePermissions } from '@/hooks/usePermissions'
import { useAbility } from '@/ability/AbilityContext'
import { canReadDashboard, normalizeCaslSubject } from '@/ability/ability'
import { useProfileStore } from '@/store/profileStore'
import { useProfileSwitch } from '@/contexts/ProfileSwitchContext'

const ENTITY_TYPES = ['ENTITY', 'ORGANIZATION']

interface ProtectedRouteProps {
  children: React.ReactNode
  redirectTo?: string
  requiredPermissions?: string[]
  requireAllPermissions?: boolean
  accessDeniedRedirect?: string
  caslAction?: string
  caslSubject?: string
  /** When set, only business client (or BC-only) users may access; others go to /orders. */
  businessClientOrderCreationOnly?: boolean
}

const BUSINESS_CLIENT_ALLOWED_PATHS = [
  '/dashboard',
  '/orders',
  '/create-order',
  '/account-settings',
  '/access-denied',
]

function isPathAllowedForBusinessClient(pathname: string): boolean {
  return BUSINESS_CLIENT_ALLOWED_PATHS.some(
    (allowed) => pathname === allowed || pathname.startsWith(allowed + '/')
  )
}

export function ProtectedRoute({
  children,
  redirectTo = '/login',
  requiredPermissions,
  requireAllPermissions = false,
  accessDeniedRedirect = '/access-denied',
  caslAction,
  caslSubject,
  businessClientOrderCreationOnly = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, loading, user } = useAuthStore()
  const { hasAnyPermission, hasAllPermissions, permissions } = usePermissions()
  const ability = useAbility()
  const location = useLocation()
  const profileMode = useProfileStore((s) => s.activeMode)
  const { canAccessBusinessClientOrderCreation } = useProfileSwitch()

  const profiles = user?.profiles ?? []
  const hasEntity = profiles.some((p) => ENTITY_TYPES.includes(p.profile_type))
  const hasBC = profiles.some((p) => p.profile_type === 'BUSINESS_CLIENT')
  const effectiveBC = profileMode === 'business_client' || (!hasEntity && hasBC && profiles.length > 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />
  }

  if (
    businessClientOrderCreationOnly &&
    !canAccessBusinessClientOrderCreation
  ) {
    return <Navigate to="/orders" replace />
  }

  if (effectiveBC) {
    if (!isPathAllowedForBusinessClient(location.pathname)) {
      return <Navigate to="/dashboard" replace />
    }
    return <>{children}</>
  }

  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasNoPermissionsYet = permissions.length === 0
    
    if (!hasNoPermissionsYet) {
      const hasPermission = requireAllPermissions
        ? hasAllPermissions(requiredPermissions)
        : hasAnyPermission(requiredPermissions)

      if (!hasPermission) {
        return <Navigate to={accessDeniedRedirect} replace />
      }
    }
  }

  if (caslAction && caslSubject) {
    const action = caslAction.toLowerCase()
    const subject = normalizeCaslSubject(caslSubject)

    if (subject === 'Dashboard' && action === 'read') {
      if (!canReadDashboard(ability)) {
        return <Navigate to={accessDeniedRedirect} replace />
      }
    } else if (!ability.can(action, subject)) {
      return <Navigate to={accessDeniedRedirect} replace />
    }
  }

  return <>{children}</>
}
